import { AxiosRequestConfig } from 'axios';
import { HttpResponse, ajax } from '../../../../common/ajax';
import { config } from '../../config';
import { CardFormFactor, CardType } from '../../enums';
import {
  AvailableCardTypeModel,
  BuyCardCommandData,
  ComputedCardProductPriceModel,
  CustomerAddressModel,
  DisableEmployeeCardCommandData,
  EmployeeCardTopUpBaseDetailsModel,
  EmployeeCardTopUpPageDetailsModel,
  EmployeeCardTransactionItemModel,
  EmployeeCardsListModel,
  TopUpCardCommandData,
  ValidateCardProductOptionsCommandData,
} from '../../models/Cards';
import { CheckoutResponseModel } from '../../models/Checkout';
import { TopUpContextType } from '../../Pages/MyAccount/CardsPage/Common/types';

interface ICardService {
  getCards: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EmployeeCardsListModel>>;
  getCardTransactions: (
    cardId: number,
    endDate?: Date,
    pageSize?: number,
    pageNumber?: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EmployeeCardTransactionItemModel[]>>;
  getAvailableCardTypesToBuy: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<AvailableCardTypeModel[]>>;
  getCustomerAddress: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<CustomerAddressModel>>;
  getTopUpCardDetailsByCard: (
    cardId: number,
    contextType: TopUpContextType,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EmployeeCardTopUpPageDetailsModel>>;
  getTopUpCardDetailsByCardType: (
    cardTypeId: number,
    contextType: TopUpContextType,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EmployeeCardTopUpBaseDetailsModel>>;
  getTopUpCardDetailsByProduct: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EmployeeCardTopUpBaseDetailsModel>>;
  topUpCard: (
    data: TopUpCardCommandData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<CheckoutResponseModel>>;
  buyCard: (
    data: BuyCardCommandData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<CheckoutResponseModel>>;
  disableCard: (
    data: DisableEmployeeCardCommandData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  validateOptions: (
    data: ValidateCardProductOptionsCommandData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  calculatePrice: (
    productId: number,
    selectedOptions: any,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ComputedCardProductPriceModel>>;
  getFormFactorOptionType: (
    cardTypeId: CardType,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<CardFormFactor[]>>;
}

export const cardService: ICardService = {
  getCards(axiosConfig) {
    return ajax.get<EmployeeCardsListModel>(
      `${config.benefitApiUrl}/employee-cards`,
      axiosConfig,
    );
  },
  getAvailableCardTypesToBuy(axiosConfig) {
    return ajax.get<AvailableCardTypeModel[]>(
      `${config.benefitApiUrl}/employee-cards/available-to-buy`,
      axiosConfig,
    );
  },
  getCustomerAddress(axiosConfig) {
    return ajax.get<CustomerAddressModel>(
      `${config.benefitApiUrl}/employee-cards/address`,
      axiosConfig,
    );
  },
  getTopUpCardDetailsByCard(cardId, contextType, axiosConfig) {
    return ajax.get<EmployeeCardTopUpPageDetailsModel>(
      `${config.benefitApiUrl}/employee-cards/top-up/cards/${cardId}?contextType=${contextType}`,
      axiosConfig,
    );
  },
  getTopUpCardDetailsByCardType(cardTypeId, contextType, axiosConfig) {
    return ajax.get<EmployeeCardTopUpBaseDetailsModel>(
      `${config.benefitApiUrl}/employee-cards/top-up/card-types/${cardTypeId}?contextType=${contextType}`,
      axiosConfig,
    );
  },
  getTopUpCardDetailsByProduct(productId, axiosConfig) {
    return ajax.get<EmployeeCardTopUpBaseDetailsModel>(
      `${config.benefitApiUrl}/employee-cards/top-up/products/${productId}`,
      axiosConfig,
    );
  },
  getCardTransactions(
    cardId: number,
    endDate?: Date,
    pageSize?: number,
    pageNumber?: number,
    axiosConfig?: AxiosRequestConfig,
  ) {
    const url = new URL(
      `${config.benefitApiUrl}/employee-cards/${cardId}/transactions`,
    );
    if (endDate !== null && endDate !== undefined) {
      url.searchParams.set('endDate', endDate.toDateString());
    }
    if (pageSize !== null && pageSize !== undefined) {
      url.searchParams.set('pageSize', pageSize.toString());
    }
    if (pageNumber !== null && pageNumber !== undefined) {
      url.searchParams.set('pageNumber', pageNumber.toString());
    }
    return ajax.get<EmployeeCardTransactionItemModel[]>(
      url.toString(),
      axiosConfig,
    );
  },
  topUpCard(data, axiosConfig) {
    return ajax.put<CheckoutResponseModel>(
      `${config.benefitApiUrl}/employee-cards/top-up`,
      data,
      axiosConfig,
    );
  },
  buyCard(data, axiosConfig) {
    return ajax.put<CheckoutResponseModel>(
      `${config.benefitApiUrl}/employee-cards/buy`,
      data,
      axiosConfig,
    );
  },
  disableCard(data, axiosConfig) {
    return ajax.post<boolean>(
      `${config.benefitApiUrl}/employee-cards/reissue/${data.cardId}`,
      data,
      axiosConfig,
    );
  },
  validateOptions(data, axiosConfig) {
    return ajax.post<boolean>(
      `${config.benefitApiUrl}/employee-cards/validate-options`,
      data,
      axiosConfig,
    );
  },
  calculatePrice(
    productId: number,
    selectedOptions: any,
    axiosConfig?: AxiosRequestConfig,
  ) {
    return ajax.post<ComputedCardProductPriceModel>(
      `${config.benefitApiUrl}/employee-cards/price`,
      { productId, selectedOptions },
      axiosConfig,
    );
  },
  getFormFactorOptionType(cardTypeId, axiosConfig) {
    return ajax.get<CardFormFactor[]>(
      `${config.benefitApiUrl}/employee-cards/form-factor/${cardTypeId}`,
      axiosConfig,
    );
  },
};
